// SearchModal on Soft — the port of the approved lab search (style lab, round
// 13: lab commit 89ccc1008, pages/lab/variants/v3-search). A centered modal
// over a dim backdrop, top to bottom:
//   the top row — the platform capsules (All + the five sources, multi-select)
//     left; right, as high as the capsules, the product's Quick buy and the
//     gear of its settings (it opens Trade settings — the saved presets P1–P3
//     trade on — over this modal);
//   the field — the search glyph, the query, Clear, the Esc key;
//   one row over the columns — the count left, then the labels Market cap ·
//     24h Vol · Liquidity, each a button that ORDERS THE ROWS SHOWN (high → low
//     first, the active label again flips it; never a request);
//   the rows — the avatar with its ring and badge, ticker + name + copy over
//     age + socials + Search on X · market cap over the 24h change · volume ·
//     liquidity · Buy (the accent only on the row under the cursor);
//   the foot — the Move / Open keys left; right, "Sort by" + the four SERVER
//     sorts (single-select, the active one again returns to relevance).
// Opened from the header search field or the "/" shortcut. Closes on the close
// affordances (backdrop click, Escape, the Esc key in the field) and on
// selecting a result.
//
// LIVE data — the data layer is preserved, not downgraded to a sample shell:
//   • results come from /v1/search (useSearch), debounced + abortable. A hit is
//     one POOL of a matched token, and the backend serves every display field
//     per pool (identity, dex, liquidity, volume, mcap, 24h change, age), so a
//     row renders complete with no per-pool /v1/pairs round-trip;
//   • an empty first-use state shows the live Top Tokens board; after a real
//     result navigation it resolves only that last-opened token afresh;
//   • address paste + Enter navigates straight to /pair/<addr>.
// The source pills (multi-select) and the four sort affordances drive the
// /v1/search request: pills scope the match set server-side (default = TON
// DEX-traded only), sort re-ranks it server-side (default relevance = pool
// liquidity, dust sunk beneath a soft $10 floor). Typed results render in server
// order until a column label orders them.
//
// MONEY PATH: a row's Buy is THE LISTS' BUY (operator 2026-10-12: "make Buy
// work as in the lists"). The modal itself buys nothing and imports nothing
// that does: like the Discover table it is handed `onGuestBuy` / `onBuy`
// (the header, through useSearchBuy), and a row's Buy does what a Discover
// row's does —
//   • a pool not quoted in TON: its page is read-only; the pill says View and
//     opens it;
//   • a guest (onGuestBuy): the login prompt; no page opens, nothing is armed,
//     and the modal closes so that the prompt can be pressed;
//   • signed in (onBuy): lib/listBuy — the one function Discover's rows call —
//     with THIS modal's Quick buy amount and trading preset: the token page
//     opens with the buy armed and the Rail sends it. No amount: the amount
//     toast, and the page opens with nothing armed;
//   • signed in, the buy's function not here yet (`buyLoad` — it is loaded on
//     demand): the press WAITS for it, its pill busy, and is then that same
//     Buy; if the function did not load, the fail toast says so and the page
//     opens with nothing armed. Never a pill that says it buys and silently
//     does not (review of round 5);
//   • none of these given (the phone shell): the token page opens with nothing
//     armed, as before Search could buy.
// The Quick buy starts EMPTY, as the lists' does: a list buy is never sent
// with a default. (The pill said "Buy 5" from the first open while it was a
// label only; live, that would be a 5 GRAM buy nobody typed.) A click on the
// row itself, the arrow keys and Enter only ever open the token page.

import {
  ArrowDownIcon,
  ArrowElbowDownLeftIcon,
  ArrowUpIcon,
  ChartBarIcon,
  ClockCounterClockwiseIcon,
  CopyIcon,
  DropIcon,
  GearSixIcon,
  MagnifyingGlassIcon,
  TrendUpIcon,
  XIcon,
} from '@phosphor-icons/react'
import {
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import type { SortParam } from '../../api/search'
import type { PriceWindowSnapshot, SearchHit, SocialLink, TokenSummary } from '../../api/types'
import { useNowMs } from '../../hooks/useNow'
import { useSearch } from '../../hooks/useSearch'
import { useSearchDefaultFeed } from '../../hooks/useSearchDefaultFeed'
import { formatCompact, formatPct, shortAddr, shouldDisplayTicker } from '../../lib/format'
import { quoteLabel } from '../../lib/pairQuote'
import {
  cancelPairRoutePrefetch,
  commitPairRoutePrefetch,
  schedulePairRoutePrefetch,
} from '../../lib/pairRoutePrefetch'
import {
  pairRouteNavigationState,
  primaryPairPath,
  searchPairPath,
  searchPairSeedRef,
} from '../../lib/pairRoutes'
import { recoveryClipsWindowStart } from '../../lib/priceWindowProvenance'
import { formatTokenAge } from '../../lib/tokenAge'
import { isNonTonQuote } from '../../lib/tonQuote'
import soft from '../../styles/soft.module.css'
import { ErrorBanner } from '../common/ErrorBanner'
import { SocialLinks } from '../common/SocialLinks'
import { canonicalizeMirroredTokenAssetURL } from '../common/tokenImage'
import { TradeSettingsDialog } from '../trade/TradeSettingsDialog'
import { Described } from '../ui/Described'
import { PlatformTip } from '../ui/PlatformTip'
import { QuickBuy } from '../ui/QuickBuy'
import { TokenAvatar } from '../ui/TokenAvatar'
import { useCopyToast } from '../ui/copyToast'
import { figs } from '../ui/fig'
import { GramMark } from '../ui/icons/GramMark'
import { SourceLogo } from '../ui/icons/SourceLogo'
import { useOptionalPixelToast } from '../ui/pixel-toast'
import styles from './search.module.css'
import type { SearchBuy, SearchBuyProps } from './useSearchBuy'

// onGuestBuy / onBuy / buyLoad: what a row's Buy does — MONEY PATH, above. The
// header gets them from useSearchBuy.
interface Props extends SearchBuyProps {
  open: boolean
  onClose: () => void
}

/** A Buy as it was pressed: the row, and the amount and the trading preset the
 *  Quick buy was on then — what its pill said — and WHEN (MONEY PATH: the
 *  buy's deadline counts from the press, also for a press that waited). */
interface BuyPress {
  row: Row
  amount: string
  preset: number
  pressedAt: number
}

const DASH = '—'
const ADDR_REGEX = /^(EQ|UQ|0:)[A-Za-z0-9_-]+$/
const SEARCH_SELECTION_KEY = 'wt:search:last-opened:v1'

interface SearchSelection {
  master: string
  pool: string
  dex?: string
}

function readSearchSelection(): SearchSelection | null {
  try {
    const raw = window.sessionStorage.getItem(SEARCH_SELECTION_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<SearchSelection>
    if (typeof value.master !== 'string' || !value.master.trim()) return null
    if (typeof value.pool !== 'string' || !value.pool.trim()) return null
    return {
      master: value.master,
      pool: value.pool,
      dex: typeof value.dex === 'string' && value.dex.trim() ? value.dex : undefined,
    }
  } catch {
    return null
  }
}

function writeSearchSelection(value: SearchSelection): void {
  try {
    window.sessionStorage.setItem(SEARCH_SELECTION_KEY, JSON.stringify(value))
  } catch {
    // Navigation remains the source of truth when storage is unavailable.
  }
}

// Source-filter pills (X6C-0). Keys are the SourceLogo / sourceKey() vocabulary
// so a token's `launchpad_source ?? dex` matches a pill verbatim.
const SOURCES: { key: string; label: string }[] = [
  { key: 'uranus', label: 'Uranus' },
  { key: 'tonfun', label: 'Tonfun' },
  { key: 'blum', label: 'Blum' },
  { key: 'dedust', label: 'DeDust' },
  { key: 'stonfi', label: 'STON.fi' },
]

// Sort buttons map to backend `sort` values — each toggles a server re-rank of
// the whole match set (not a client reshuffle of one page). The default is
// 'relevance' (no button active): exact symbol/name first, then TON liquidity.
type SortButton = Exclude<SortParam, 'relevance'>
const SORTS: { key: SortButton; Icon: typeof DropIcon; label: string }[] = [
  { key: 'change', Icon: TrendUpIcon, label: 'Sort by 24h change' },
  { key: 'volume', Icon: ChartBarIcon, label: 'Sort by volume' },
  { key: 'liquidity', Icon: DropIcon, label: 'Sort by liquidity' },
  { key: 'age', Icon: ClockCounterClockwiseIcon, label: 'Sort by recency' },
]

// The column labels order the rows ALREADY SHOWN — never a request (the foot's
// "Sort by" is the one that asks the server). A click orders high → low, the
// active label again flips it; null = the order the rows came in.
type OrderKey = 'mcap' | 'volume' | 'liquidity'
export type RowOrder = { key: OrderKey; dir: 'desc' | 'asc' } | null
const COLUMNS: { key: OrderKey; label: string; name: string }[] = [
  { key: 'mcap', label: 'Market cap', name: 'Order by market cap' },
  { key: 'volume', label: '24h Vol', name: 'Order by 24h volume' },
  { key: 'liquidity', label: 'Liquidity', name: 'Order by liquidity' },
]

// Normalized display row — every field already resolved for render, sourced from
// (in order of completeness) /v1/pairs detail → trending feed → bare search hit.
export interface Row {
  master: string
  // pool = the specific pool this row represents (a token with several pools
  // yields several rows). For an empty-state / bonding row it is the master. It
  // is the React key and drives per-pool routing + enrichment.
  pool: string
  ticker: string
  name: string
  imageUrl?: string
  socialLinks?: SocialLink[]
  dex?: string
  launchpadSource?: string | null
  launchpadStatus?: string | null
  bondingPct?: number | null
  mcap: number | null
  change: number | null
  priceWindow?: PriceWindowSnapshot
  volNative: number | null
  volQuote: number | null
  // Backend-served USD volume for a pool row (empty-state rows use volNative/rate).
  volUsd?: number | null
  quoteTicker?: string | null
  // The pool's quote master. A row of a pool without TON opens that pool.
  quoteToken?: string | null
  priceUsd: number | null
  lastPrice: number | null
  liq: number | null
  discoveredAt?: string
}

export function SearchModal({ open, onClose, onGuestBuy, onBuy, buyLoad }: Props) {
  const ref = useRef<HTMLDialogElement>(null)
  const downOnBackdrop = useRef(false)
  // Trade settings (the saved presets a list buy goes out on), opened by the
  // Quick buy's gear on the preset the Quick buy is on; null = shut. It is
  // held HERE and drawn beside the search dialog, never inside it: React hands
  // a dialog's `close` up its tree, so a settings dialog drawn inside would
  // shut the search with itself. (The shared dialog hands the focus back to
  // the gear when it shuts: ui/FilterDialog.)
  const [settingsPreset, setSettingsPreset] = useState<number | null>(null)
  const closeSettings = () => setSettingsPreset(null)
  // the search shut: its settings shut with it
  useEffect(() => {
    if (!open) setSettingsPreset(null)
  }, [open])

  // Native <dialog> drives modality (focus-trap, top-layer, Escape→onClose); we
  // add body-scroll-lock. showModal/close follow the `open` prop.
  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open) {
      if (!d.open) d.showModal()
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = prev
      }
    }
    if (d.open) d.close()
  }, [open])

  return (
    <>
      {/* Click on the dialog itself = the ::backdrop area → close. Keyboard
          dismiss is the native <dialog> Escape (fires onClose), so no key
          handler is needed. */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop pointer-dismiss; keyboard close is native Escape */}
      <dialog
        ref={ref}
        className={styles.dialog}
        aria-label="Search"
        onClose={onClose}
        onMouseDown={(e) => {
          downOnBackdrop.current = e.target === ref.current
        }}
        onClick={(e) => {
          if (e.target === ref.current && downOnBackdrop.current) onClose()
        }}
      >
        {open && (
          <SearchBody
            onClose={onClose}
            onGuestBuy={onGuestBuy}
            onBuy={onBuy}
            buyLoad={buyLoad}
            onOpenSettings={setSettingsPreset}
          />
        )}
      </dialog>
      {open && settingsPreset !== null && (
        <TradeSettingsDialog preset={settingsPreset} onClose={closeSettings} />
      )}
    </>
  )
}

function SearchBody({
  onClose,
  onGuestBuy,
  onBuy,
  buyLoad,
  onOpenSettings,
}: Omit<Props, 'open'> & { onOpenSettings: (preset: number) => void }) {
  const navigate = useNavigate()
  const toast = useOptionalPixelToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  // where the pointer last was over a row (see hoverRow)
  const pointerAt = useRef<{ x: number; y: number } | null>(null)
  const [query, setQuery] = useState('')
  const [activeSources, setActiveSources] = useState<Set<string>>(new Set())
  const [sort, setSort] = useState<SortParam>('relevance')
  // The Quick buy: the amount a row's Buy carries (empty until typed, as in
  // the lists: no default is ever bought) and the trading preset it goes out
  // on (the first by default). MONEY PATH — buyRow, below.
  const [buyAmount, setBuyAmount] = useState('')
  const [buyPreset, setBuyPreset] = useState(0)
  // a Buy pressed while the buy's function was still loading (buyRow, below)
  const [waitingBuy, setWaitingBuy] = useState<BuyPress | null>(null)
  // The order a column label puts the shown rows in; null = as they came.
  const [order, setOrder] = useState<RowOrder>(null)
  // This is user intent, not a data cache: only route identity is retained.
  // Every later row is resolved again from the authoritative API.
  const [lastOpened] = useState<SearchSelection | null>(readSearchSelection)
  // -1 = nothing highlighted (idle); set only by hover / arrow keys.
  const [sel, setSel] = useState(-1)
  const q = query.trim()
  const hasInput = q.length > 0
  const defaultTopMode = !hasInput && lastOpened == null

  // Stable comma-separated scope key — feeds both the memo dep and the request.
  const sourcesCsv = useMemo(() => Array.from(activeSources).sort().join(','), [activeSources])

  const hasQuery = q.length >= 2
  const recentQuery = !hasInput ? (lastOpened?.master ?? '') : ''
  const {
    results,
    loading,
    error: searchError,
    retry: retrySearch,
  } = useSearch(
    hasQuery ? query : recentQuery,
    hasQuery ? sort : 'relevance',
    hasQuery ? sourcesCsv : ''
  )
  const defaultFeed = useSearchDefaultFeed(30, defaultTopMode)

  const addrQuery = hasQuery && ADDR_REGEX.test(q) ? q : null
  const hits = useMemo<SearchHit[]>(
    // A failed search is no answer: no stand-in row for the address.
    () => addressFallbackHits(results, addrQuery, loading, searchError),
    [results, addrQuery, loading, searchError]
  )
  const displayHits = useMemo(() => hits.filter(searchHitIdentityReady), [hits])
  const identityPending = hasQuery && !loading && results.length > 0 && displayHits.length === 0
  const unknownAddress =
    addrQuery != null && !loading && searchError == null && results.length === 0

  const served = useMemo<Row[]>(() => {
    // Typed query: the backend already scoped (platform pills), expanded each
    // token into its pools, ranked (sort) the full set, and served every display
    // metric per pool — render its order verbatim, no client reshuffle or
    // enrichment. A low-liquidity clone can't outrank the real token.
    if (hasQuery) return displayHits.map(rowFromHit)
    if (hasInput) return []
    if (lastOpened != null) {
      const exact = displayHits.find((hit) => hit.pool_addr === lastOpened.pool)
      const selected = exact ?? displayHits.find((hit) => hit.jetton_master === lastOpened.master)
      return selected ? [rowFromHit(selected)] : []
    }
    return defaultFeed.tokens.map(rowFromToken)
  }, [hasQuery, hasInput, displayHits, lastOpened, defaultFeed.tokens])
  // The served rows, in the order of a column label when one is on.
  const live = useMemo(() => orderRows(served, order), [served, order])
  // THE ROWS DO NOT MOVE UNDER THE POINTER. Both lists move by themselves: Top
  // tokens is re-ranked on every trade and its membership is replaced, and a
  // typed list in a column label's order follows its live figures. A row that
  // slides under the pointer at the moment of a click is another token opened
  // — and, where a row's Buy buys, another token bought. So while the pointer
  // is over the list its rows keep their places (heldRowsOf): each is drawn
  // with its fresh figures, one that left the list stays as it was last, a new
  // one waits. The hold ends when the pointer leaves, and starts anew when the
  // list is asked for anew (the query, a platform, a sort, a column label) —
  // an empty list is never held: it is an answer on its way. The Discover
  // table holds its rows the same way (DiscoverPage: `paused`).
  const [held, setHeld] = useState(false)
  const heldRows = useRef<{ key: string; rows: Row[] } | null>(null)
  const listKey = [
    hasInput ? q : lastOpened == null ? 'top' : 'last',
    sort,
    sourcesCsv,
    order ? `${order.key}:${order.dir}` : '',
  ].join('|')
  // What is drawn — and what the arrow keys and Enter walk.
  const shown = useMemo(() => {
    if (!held) {
      heldRows.current = null
      return live
    }
    if (heldRows.current == null || heldRows.current.key !== listKey) {
      if (live.length === 0) return live
      heldRows.current = { key: listKey, rows: live }
    }
    return heldRowsOf(heldRows.current.rows, live)
  }, [held, live, listKey])

  // Focus the field after the dialog has actually opened — the parent showModal()
  // effect runs AFTER this child effect, so defer a frame or the focus is dropped
  // inside the still-closed dialog (codex review).
  useEffect(() => {
    const id = requestAnimationFrame(() => inputRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-runs to clear the keyboard selection whenever the result set is reshaped (query / sort / source filter / a column label's order)
  useEffect(() => {
    setSel(-1)
  }, [query, sort, activeSources, order])
  // MONEY PATH — a Buy that waits (buyRow) belongs to the list it was pressed
  // in. When another list is asked for (the query, a platform, a server sort)
  // its pill is gone with its row, and the press is forgotten with it: it must
  // not go out from under a list that no longer shows what was pressed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-runs whenever another list is asked for
  useEffect(() => {
    setWaitingBuy(null)
  }, [query, sort, activeSources])

  const selClamped = sel < 0 ? -1 : Math.min(sel, shown.length - 1)

  const openRow = (r: Row) => {
    navigate(searchPairPath(r), { state: pairRouteNavigationState(r.pool) })
    writeSearchSelection({ master: r.master, pool: r.pool, dex: r.dex })
    onClose()
  }

  // the lists' buy, with the amount and the preset of the press; it opens the page
  const buyNow = (buy: SearchBuy, press: BuyPress) => {
    buy(press.row, press.amount, press.preset, press.pressedAt)
    writeSearchSelection({ master: press.row.master, pool: press.row.pool, dex: press.row.dex })
    onClose()
  }
  // the buy's function did not load: said, and the token opens with nothing armed
  const openUnbought = (r: Row) => {
    toast?.push({ tone: 'fail', content: 'Buy did not start — try again', duration: 5000 })
    openRow(r)
  }

  // MONEY PATH — a row's Buy, in the order a Discover row decides it (the
  // table's action cell, then DiscoverPage's onListBuy).
  const buyRow = (r: Row) => {
    // a pool not quoted in TON: its page is read-only, its pill says View
    if (isNonTonQuote(r.quoteToken)) {
      openRow(r)
      return
    }
    // A guest is asked to log in: no page opens, nothing is armed. The modal
    // closes: the prompt is a toast, and while a modal dialog is open nothing
    // outside it can be pressed — "Log in" would be drawn and dead (measured
    // in the browser).
    if (onGuestBuy) {
      onGuestBuy()
      onClose()
      return
    }
    const press: BuyPress = {
      row: r,
      amount: buyAmount,
      preset: buyPreset,
      pressedAt: Date.now(),
    }
    if (onBuy) {
      buyNow(onBuy, press)
      return
    }
    // The buy's function is on its way: the press waits for it (answered
    // below). The pill that waits cannot be pressed again; another row's Buy
    // takes its place.
    if (buyLoad === 'loading') {
      if (waitingBuy?.row.pool !== r.pool) setWaitingBuy(press)
      return
    }
    if (buyLoad === 'failed') {
      openUnbought(r)
      return
    }
    // no buy on this surface: the token page, with nothing armed
    openRow(r)
  }

  // MONEY PATH — the press that waited is answered here, ONCE: the lists' buy
  // with the amount and the preset its pill said when it was pressed, or — the
  // function did not load — the fail toast and the page with nothing armed. A
  // press belongs to this open modal: closing it forgets the press.
  const answerBuy = (press: BuyPress) => {
    if (onBuy) buyNow(onBuy, press)
    else if (buyLoad === 'failed') openUnbought(press.row)
  }
  const answerBuyRef = useRef(answerBuy)
  answerBuyRef.current = answerBuy
  const answeredBuy = useRef<BuyPress | null>(null)
  useEffect(() => {
    if (!waitingBuy || answeredBuy.current === waitingBuy) return
    if (!onBuy && buyLoad === 'loading') return
    answeredBuy.current = waitingBuy
    setWaitingBuy(null)
    answerBuyRef.current(waitingBuy)
  }, [waitingBuy, onBuy, buyLoad])

  // Hover/commit intent starts the pair-seed prefetch on the exact (addr, mode)
  // openRow will navigate to — searchPairSeedRef and searchPairPath are two
  // views of one branch set, so the prefetch key cannot drift from the route.
  const prefetchRow = (r: Row, intent: 'hover' | 'commit' | 'cancel') => {
    const ref = searchPairSeedRef(r)
    if (intent === 'commit') commitPairRoutePrefetch(ref.addr, ref.mode)
    else if (intent === 'cancel') cancelPairRoutePrefetch(ref.addr, ref.mode)
    else schedulePairRoutePrefetch(ref.addr, ref.mode)
  }

  // The arrow keys move the cursor AND keep its row in view. Before, the list
  // never scrolled: past the last row that fits, the cursor left the window
  // and Enter opened a row nobody could see. The list scrolls by what it takes
  // (block: 'nearest') and only for a key, never for a hover. The rows are
  // already in the DOM and the cursor changes no row's size, so there is
  // nothing to wait a render for.
  const moveCursor = (next: number) => {
    setSel(next)
    listRef.current
      ?.querySelector<HTMLElement>(`[data-row-index="${next}"]`)
      ?.scrollIntoView?.({ block: 'nearest' })
  }

  // The pointer moves the cursor only when the pointer itself moved. A row
  // that slides under a pointer standing still — the list just scrolled to
  // follow the arrow keys — would otherwise take the cursor straight back from
  // the keyboard (the browser reports the row under a resting pointer anew
  // after a scroll, at the same coordinates).
  const hoverRow = (index: number, e: MouseEvent) => {
    const prev = pointerAt.current
    pointerAt.current = { x: e.clientX, y: e.clientY }
    if (prev && prev.x === e.clientX && prev.y === e.clientY) return
    setSel(index)
  }

  const toggleSource = (key: string) => {
    setActiveSources((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      moveCursor(Math.min(shown.length - 1, selClamped + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      moveCursor(Math.max(0, selClamped - 1))
    } else if (e.key === 'Enter') {
      // Address paste short-circuits search → straight to the pair page.
      if (ADDR_REGEX.test(q)) {
        navigate(primaryPairPath(q))
        onClose()
        return
      }
      const r = shown[selClamped < 0 ? 0 : selClamped]
      if (r) openRow(r)
    }
  }

  return (
    <div
      className={styles.surface}
      data-search-default-mode={
        hasInput ? 'query' : lastOpened == null ? 'top-tokens' : 'last-opened'
      }
      // how wide the Buy lane is: the Quick buy's amount, to ten characters
      style={{ ['--buy-ch' as string]: Math.min(buyAmount.length, 10) }}
    >
      {/* ---- The top row: the platforms (multi-select) left; the Quick buy and
          its settings right ---- */}
      <div className={styles.top}>
        <fieldset className={styles.sources} aria-label="Platforms">
          {/* All = no platform chosen: the request the modal opens with */}
          <button
            type="button"
            className={styles.source}
            aria-pressed={activeSources.size === 0}
            onClick={() => setActiveSources((prev) => (prev.size === 0 ? prev : new Set()))}
          >
            {figs('All')}
          </button>
          {SOURCES.map((s) => (
            <button
              key={s.key}
              type="button"
              className={styles.source}
              aria-pressed={activeSources.has(s.key)}
              onClick={() => toggleSource(s.key)}
            >
              <span className={styles.mark} data-src={s.key}>
                <SourceLogo source={s.key} size={16} />
              </span>
              {figs(s.label)}
            </button>
          ))}
        </fieldset>
        <div className={styles.settings}>
          {/* Empty, it says "0.0", as a column head's does: the field here is
              five figures wide (the row has no more), and the word "Amount"
              was cut in it — "Amoun". */}
          <QuickBuy
            amount={buyAmount}
            onAmountChange={setBuyAmount}
            preset={buyPreset}
            onPresetChange={setBuyPreset}
            placeholder="0.0"
            className={styles.quick}
          />
          {/* The gear: Trade settings — what P1–P3 trade on, the one saved set
              the lists, the form and Instant trade read — on the preset this
              Quick buy is on. */}
          <Described
            as="button"
            type="button"
            className={`${soft.btn} ${soft.round} ${soft.small} ${styles.gear}`}
            aria-label="Quick buy settings"
            aria-haspopup="dialog"
            description="Trade settings — what P1–P3 trade on"
            onClick={() => onOpenSettings(buyPreset)}
          >
            <GearSixIcon size={16} weight="bold" aria-hidden />
          </Described>
        </div>
      </div>

      {/* ---- The field ---- */}
      <label className={`${soft.field} ${styles.field}`}>
        <MagnifyingGlassIcon size={18} weight="bold" aria-hidden />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search by name, ticker, or contract address"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          spellCheck={false}
          autoComplete="off"
          aria-label="Search tokens"
        />
        <span className={styles.fieldEnd}>
          {query && (
            <button
              type="button"
              className={styles.clear}
              aria-label="Clear search"
              onClick={() => {
                setQuery('')
                inputRef.current?.focus()
              }}
            >
              <XIcon size={14} weight="bold" aria-hidden />
            </button>
          )}
          <button type="button" className={styles.key} aria-label="Close search" onClick={onClose}>
            {figs('Esc')}
          </button>
        </span>
      </label>

      {/* ---- One row over the columns: what the list is (the count of a typed
          search) left, then the labels — each orders the rows shown ---- */}
      <div className={`${styles.lanes} ${styles.head}`}>
        <output className={styles.count}>
          {hasQuery && !loading && !searchError ? (
            <>
              <b>{shown.length}</b> {shown.length === 1 ? 'result' : 'results'}
            </>
          ) : (
            // no number before there is an answer (one character searches
            // nothing; a request in flight or a failed one has no count)
            headLabel(hasInput, lastOpened != null)
          )}
        </output>
        {COLUMNS.map(({ key, label, name }) => {
          const dir = order?.key === key ? order.dir : undefined
          return (
            <button
              key={key}
              type="button"
              className={styles.sort}
              aria-pressed={dir != null}
              aria-label={name}
              data-dir={dir}
              onClick={() =>
                setOrder((prev) =>
                  prev?.key === key
                    ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
                    : { key, dir: 'desc' }
                )
              }
            >
              {figs(label)}
              {dir === 'asc' ? (
                <ArrowUpIcon size={12} weight="bold" aria-hidden />
              ) : (
                <ArrowDownIcon size={12} weight="bold" aria-hidden />
              )}
            </button>
          )
        })}
        <span aria-hidden="true" />
      </div>

      {/* ---- The rows ---- */}
      <div
        ref={listRef}
        className={styles.list}
        // the pointer over the list holds its rows in their places
        onMouseEnter={() => setHeld(true)}
        onMouseLeave={() => {
          setHeld(false)
          setSel(-1)
        }}
      >
        <ResultList
          rows={shown}
          hasQuery={hasQuery || hasInput}
          searched={hasQuery}
          loading={hasQuery ? loading : false}
          searchError={searchError}
          onRetrySearch={retrySearch}
          trendingLoading={!hasInput && (lastOpened == null ? defaultFeed.loading : loading)}
          trendingError={defaultTopMode && defaultFeed.error}
          defaultTopMode={defaultTopMode}
          query={q}
          identityPending={identityPending}
          unknownAddress={unknownAddress}
          selected={selClamped}
          buyAmount={buyAmount}
          waitingBuy={waitingBuy}
          onHover={hoverRow}
          onOpen={openRow}
          onBuy={buyRow}
          onPrefetchRow={prefetchRow}
        />
      </div>

      {/* ---- The foot: the keys left; right, the server sorts of a typed
          search (each asks the server again; the active one again returns to
          relevance). A server sort takes a column label's order off. ---- */}
      <div className={styles.foot}>
        <span className={styles.hints}>
          <span className={styles.hint}>
            <kbd className={`${styles.key} ${styles.keyIcon}`}>
              <ArrowUpIcon size={12} weight="bold" aria-hidden />
              <span className={styles.srOnly}>Up</span>
            </kbd>
            <kbd className={`${styles.key} ${styles.keyIcon}`}>
              <ArrowDownIcon size={12} weight="bold" aria-hidden />
              <span className={styles.srOnly}>Down</span>
            </kbd>
            {figs('Move')}
          </span>
          <span className={styles.hint}>
            <kbd className={`${styles.key} ${styles.keyIcon}`}>
              <ArrowElbowDownLeftIcon size={12} weight="bold" aria-hidden />
              <span className={styles.srOnly}>Enter</span>
            </kbd>
            {figs('Open')}
          </span>
        </span>
        {hasQuery && (
          <div className={styles.sortGroup}>
            <span className={styles.sortBy}>Sort by</span>
            <fieldset className={`${soft.track} ${styles.sortTrack}`} aria-label="Sort results">
              {SORTS.map(({ key, Icon, label }) => (
                <button
                  key={key}
                  type="button"
                  className={soft.seg}
                  aria-pressed={key === sort}
                  aria-label={label}
                  onClick={() => {
                    setSort((prev) => (prev === key ? 'relevance' : key))
                    setOrder(null)
                  }}
                >
                  <Icon size={16} weight="bold" aria-hidden />
                </button>
              ))}
            </fieldset>
          </div>
        )}
      </div>
    </div>
  )
}

// What the list is, where the count of a typed search stands: the two lists an
// empty field shows, or — before a typed search has an answer — just "Results".
function headLabel(hasInput: boolean, hasLastOpened: boolean): string {
  if (hasInput) return 'Results'
  return hasLastOpened ? 'Last opened' : 'Top tokens'
}

function ResultList({
  rows,
  hasQuery,
  searched,
  loading,
  searchError,
  onRetrySearch,
  trendingLoading,
  trendingError,
  defaultTopMode,
  query,
  identityPending,
  unknownAddress,
  selected,
  buyAmount,
  waitingBuy,
  onHover,
  onOpen,
  onBuy,
  onPrefetchRow,
}: {
  rows: Row[]
  hasQuery: boolean
  /** a search was really asked for (two characters or more) */
  searched: boolean
  loading: boolean
  searchError: Error | null
  onRetrySearch: () => void
  trendingLoading: boolean
  trendingError: boolean
  defaultTopMode: boolean
  query: string
  identityPending: boolean
  unknownAddress: boolean
  selected: number
  buyAmount: string
  /** the Buy that waits for the buy's function: its row's pill is busy */
  waitingBuy: BuyPress | null
  onHover: (i: number, e: MouseEvent) => void
  onOpen: (r: Row) => void
  onBuy: (r: Row) => void
  onPrefetchRow: (r: Row, intent: 'hover' | 'commit' | 'cancel') => void
}) {
  if (rows.length === 0) {
    return (
      <EmptyResultState
        hasQuery={hasQuery}
        searched={searched}
        loading={loading}
        searchError={searchError}
        onRetrySearch={onRetrySearch}
        trendingLoading={trendingLoading}
        trendingError={trendingError}
        defaultTopMode={defaultTopMode}
        query={query}
        identityPending={identityPending}
        unknownAddress={unknownAddress}
      />
    )
  }
  return (
    <>
      {rows.map((r, i) => {
        // a pill that waits says the amount it was pressed with: what it buys
        const waits = waitingBuy?.row.pool === r.pool
        return (
          <ResultRow
            key={r.pool}
            row={r}
            index={i}
            selected={i === selected}
            buyAmount={waits ? waitingBuy.amount : buyAmount}
            buyBusy={waits || undefined}
            onHover={(e) => onHover(i, e)}
            onOpen={() => onOpen(r)}
            onBuy={() => onBuy(r)}
            onPrefetch={(intent) => onPrefetchRow(r, intent)}
          />
        )
      })}
    </>
  )
}

// The one thing an empty list says: a line, and under it — where there is
// something to do about it — a quieter hint.
function Status({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className={styles.status}>
      <span className={styles.statusTitle}>{title}</span>
      {hint && <span className={styles.statusHint}>{hint}</span>}
    </div>
  )
}

function EmptyResultState({
  hasQuery,
  searched,
  loading,
  searchError,
  onRetrySearch,
  trendingLoading,
  trendingError,
  defaultTopMode,
  query,
  identityPending,
  unknownAddress,
}: {
  hasQuery: boolean
  searched: boolean
  loading: boolean
  searchError: Error | null
  onRetrySearch: () => void
  trendingLoading: boolean
  trendingError: boolean
  defaultTopMode: boolean
  query: string
  identityPending: boolean
  unknownAddress: boolean
}) {
  if (hasQuery) {
    if (loading) return <Status title="Searching…" />
    // A failed request is not "No results": say it failed and offer a retry.
    if (searchError)
      return (
        <div className={styles.status}>
          <ErrorBanner error="Search failed. Try again." onRetry={onRetrySearch} />
        </div>
      )
    if (identityPending) return <Status title="Token metadata is resolving…" />
    if (unknownAddress) return <Status title="Token is not indexed yet." />
    // One character asks the server nothing, so "No results" would be a claim
    // nobody checked: the lab's line for it instead.
    if (!searched) return <Status title="Keep typing — search starts at two characters." />
    return (
      <Status
        title={`No results for “${query}”`}
        hint="Try a ticker, the full name or a contract address."
      />
    )
  }
  if (defaultTopMode) {
    return <Status title={defaultTopEmptyText(trendingLoading, trendingError)} />
  }
  return (
    <Status title={trendingLoading ? 'Loading…' : 'Type a name, ticker, or contract address.'} />
  )
}

function defaultTopEmptyText(loading: boolean, error: boolean): string {
  if (loading) return 'Loading…'
  if (error) return 'Top Tokens are unavailable.'
  return 'No Top Tokens right now.'
}

// What a row's pill is. A View only opens the page (a pool not quoted in TON);
// a Buy is the lists' Buy. `action` is what the stylesheet tells them apart by
// — only a Buy takes the accent on the cursor's row — and `name` begins the
// pill's accessible name.
const VIEW_PILL = { action: 'view', name: 'View' } as const
const BUY_PILL = { action: 'buy', name: 'Trade' } as const

function ResultRow({
  row,
  index,
  selected,
  buyAmount,
  buyBusy,
  onHover,
  onOpen,
  onBuy,
  onPrefetch,
}: {
  row: Row
  /** its place in the list: what the arrow keys scroll into view */
  index: number
  selected: boolean
  /** what the Buy pill says (the Quick buy's amount); '' = a plain "Buy" */
  buyAmount: string
  /** its Buy was pressed and waits for the buy's function: busy, not pressable */
  buyBusy?: true
  onHover: (e: MouseEvent) => void
  onOpen: () => void
  /** the pill's click — MONEY PATH (SearchBody's buyRow) */
  onBuy: () => void
  onPrefetch?: (intent: 'hover' | 'commit' | 'cancel') => void
}) {
  const pct = row.change
  // a pool not quoted in TON is not bought from a list: its pill is View
  const readOnly = isNonTonQuote(row.quoteToken)
  const pill = readOnly ? VIEW_PILL : BUY_PILL
  const rowRef = useRef<HTMLDivElement>(null)
  // Gold ring only follows launchpad migration status. A still-bonding token
  // with external DEX liquidity keeps its launchpad colour.
  const migrated = row.launchpadSource != null && row.launchpadStatus === 'migrated'
  // Row is keyboard-operable (role + tabIndex + onKeyDown) but can't be a
  // <button> — it nests interactive controls (copy / socials / Buy). Applied as
  // a typed spread so the markup stays a semantic-neutral container.
  const interactive: HTMLAttributes<HTMLDivElement> & { tabIndex: number } = {
    role: 'button',
    tabIndex: 0,
    'aria-label': `Open ${row.ticker}`,
    onMouseEnter: (e) => {
      onHover(e)
      onPrefetch?.('hover')
    },
    onMouseMove: onHover,
    onPointerDown: () => onPrefetch?.('commit'),
    onPointerLeave: () => onPrefetch?.('cancel'),
    onFocus: () => onPrefetch?.('hover'),
    onBlur: () => onPrefetch?.('cancel'),
    onClick: onOpen,
    onKeyDown: (e) => {
      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault()
        onOpen()
      }
    },
  }
  return (
    <div
      ref={rowRef}
      className={`${styles.lanes} ${styles.row}`}
      data-row-index={index}
      data-selected={selected || undefined}
      {...interactive}
    >
      {/* мини-попап платформы/стадии — fixed на верхней кромке строки */}
      <PlatformTip
        token={{
          launchpad_source: row.launchpadSource,
          launchpad_status: row.launchpadStatus,
          bonding_progress_pct: row.bondingPct,
          dex: row.dex,
        }}
        anchorRef={rowRef}
        soft
      />
      {/* TOKEN — the platform is the avatar's ring and badge */}
      <div className={styles.token}>
        <TokenAvatar
          imageUrl={row.imageUrl}
          label={row.ticker}
          dex={migrated ? row.dex : (row.launchpadSource ?? row.dex)}
          launchpadSource={migrated ? row.launchpadSource : null}
          bondingPct={row.bondingPct}
          size={44}
          badgeSize={16}
          atomicImage
        />
        <div className={styles.identity}>
          <div className={styles.identTop}>
            {/* the words of two sizes on one baseline, cut to their capitals;
                the copy mark centres on them */}
            <span className={styles.identWords}>
              <span className={styles.ticker}>{row.ticker}</span>
              {row.quoteTicker && <span className={styles.quote}>/ {row.quoteTicker}</span>}
              {row.name?.trim() && <span className={styles.name}>{row.name}</span>}
            </span>
            <CopyButton value={row.master} />
          </div>
          {/* the age, the socials (the lists' glyphs, X and Telegram in their own
              colours), then Search on X — as on the token page */}
          <div className={styles.meta}>
            <Age iso={row.discoveredAt} />
            <span className={styles.socials}>
              <SocialLinks links={row.socialLinks} className={styles.social} iconSize={16} pack />
              <a
                className={styles.social}
                data-type="search"
                href={`https://x.com/search?q=${encodeURIComponent(row.master)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Search on X"
                onClick={(e) => e.stopPropagation()}
              >
                <MagnifyingGlassIcon size={16} weight="bold" aria-hidden />
              </a>
            </span>
          </div>
        </div>
      </div>

      {/* MARKET CAP over the 24h change (plain coloured text) */}
      <div className={styles.mc}>
        <span className={`${styles.value} ${row.mcap == null ? styles.muted : ''}`}>
          {usd(row.mcap)}
        </span>
        <Described
          as="span"
          className={`${styles.change} ${pctTone(pct)}`}
          description={searchPriceWindowTitle(row.priceWindow)}
        >
          {pct == null || !Number.isFinite(pct) ? DASH : formatPct(pct)}
        </Described>
      </div>

      {/* VOL — always USD (native×rate, or USDT-quoted quote vol); else muted "—" */}
      <Volume row={row} />

      {/* LIQUIDITY */}
      <span className={`${styles.value} ${styles.num} ${row.liq == null ? styles.muted : ''}`}>
        {usd(row.liq)}
      </span>

      {/* BUY — the lists' Buy (MONEY PATH, top of the file). It says the
          Quick buy's amount, which is what it buys; with none it is a plain
          Buy and arms nothing. A pool not quoted in TON says View, as its
          Discover row does. Quiet on every row, the accent on the row under
          the cursor — for a pill that buys only: data-action tells the
          stylesheet a View from a Buy, and a View stays quiet there too.
          Pressed before the buy's function has loaded, it waits for it: busy,
          and a second press does nothing (buyRow). aria-disabled, not
          `disabled`: a press on a disabled button may fall through to the
          row, which opens the page. */}
      <button
        type="button"
        className={styles.buy}
        data-action={pill.action}
        aria-label={`${pill.name} ${row.ticker}`}
        aria-busy={buyBusy}
        aria-disabled={buyBusy}
        onClick={(e) => {
          e.stopPropagation()
          onBuy()
        }}
      >
        {readOnly ? (
          figs('View')
        ) : buyAmount ? (
          <>
            <span className={styles.buyText}>Buy {buyAmount}</span>
            <GramMark size={14} knockout={selected} />
          </>
        ) : (
          figs('Buy')
        )}
      </button>
    </div>
  )
}

// Volume in USD: native × the token's own implied rate (price_usd / last_price),
// or — for a USDT-quoted pool whose native side is 0 — the quote-side volume
// (USDT ≈ USD). No honest USD figure → muted "—" (never a misleading GRAM value).
function Volume({ row }: { row: Row }) {
  const v = usdVolume(row)
  return (
    <span className={`${styles.value} ${styles.num} ${v == null ? styles.muted : ''}`}>
      {v == null ? DASH : `$${formatCompact(v)}`}
    </span>
  )
}

// A column label's order of the rows shown. A row with no figure in that column
// goes last whichever way the order runs: an unknown is neither the highest nor
// the lowest.
const ORDER_VALUE: Record<OrderKey, (row: Row) => number | null> = {
  mcap: (row) => row.mcap,
  volume: (row) => usdVolume(row),
  liquidity: (row) => row.liq,
}

export function orderRows(rows: Row[], order: RowOrder): Row[] {
  if (!order) return rows
  const value = ORDER_VALUE[order.key]
  const sign = order.dir === 'desc' ? -1 : 1
  const known = (v: number | null) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  return [...rows].sort((a, b) => {
    const av = known(value(a))
    const bv = known(value(b))
    if (av == null || bv == null) return av == null ? (bv == null ? 0 : 1) : -1
    return sign * (av - bv)
  })
}

/** The rows drawn while the pointer holds the list: the places of the rows it
 *  held, each as the list has it now — fresh figures — or, when the list no
 *  longer has it, as it was last. No row is added and none is dropped: either
 *  would move the rows under the pointer. A row is its pool. */
export function heldRowsOf(held: Row[], live: Row[]): Row[] {
  const fresh = new Map(live.map((row) => [row.pool, row]))
  return held.map((row) => fresh.get(row.pool) ?? row)
}

export function usdVolume(row: Row): number | null {
  // Pool rows carry the backend's per-pool USD volume directly; the empty-state
  // (token) rows fall back to native × implied rate / quote-side volume.
  // A backend 0 is a FACT (we index the pool; it had no trades in the window),
  // not a gap — take it as $0 instead of falling through to the "—" path. The
  // backend omits the field entirely when it has no TON/USD rate to convert with.
  if (row.volUsd != null) return row.volUsd
  const rate =
    row.priceUsd != null && row.lastPrice != null && row.lastPrice > 0
      ? row.priceUsd / row.lastPrice
      : null
  if (rate != null && row.volNative != null && row.volNative > 0) return row.volNative * rate
  if (row.volQuote != null && row.volQuote > 0 && row.quoteTicker && /usd/i.test(row.quoteTicker)) {
    return row.volQuote
  }
  return null
}

function CopyButton({ value }: { value: string }) {
  // общий стандарт (2026-08-30): тост «Token address copied», иконка в ячейке
  // не меняется — как в Discover (Phosphor Copy, bold) и на странице токена
  // ui/copyToast: the toast once the address is on the clipboard (r9x-5)
  const copy = useCopyToast()
  return (
    <Described
      as="button"
      type="button"
      className={styles.copy}
      aria-label="Copy contract"
      description={shortAddr(value)}
      onClick={(e) => {
        e.stopPropagation()
        void copy(value, 'Token address copied')
      }}
    >
      <CopyIcon size={16} weight="bold" aria-hidden />
    </Described>
  )
}

// Age — Discover standard: green (--buy-delta) when known, muted "—" otherwise.
function Age({ iso }: { iso?: string }) {
  const label = formatTokenAge(iso, useNowMs())
  return <span className={`${styles.age} ${label === DASH ? styles.ageMuted : ''}`}>{label}</span>
}

// --- row builders ----------------------------------------------------------

export function rowFromToken(t: TokenSummary): Row {
  return {
    master: t.jetton_master,
    // Top Tokens is token-ranked but still owns an exact primary route. Keep it
    // so the later one-row Search seed resolves the same pool and liquidity.
    pool: t.primary_pool_addr || t.jetton_master,
    ticker: displayTicker(t.ticker, t.jetton_master),
    name: t.name,
    imageUrl:
      canonicalizeMirroredTokenAssetURL(t.dex_image_url) ??
      safeImage(t.image_url, t.image_mirrored),
    socialLinks: t.social_links,
    dex: t.dex,
    launchpadSource: t.launchpad_source,
    launchpadStatus: t.launchpad_status,
    bondingPct: t.bonding_progress_pct ?? null,
    mcap: t.mcap_usd ?? null,
    change: t.price_change_24h_pct ?? null,
    priceWindow: t.price_window,
    // A pool row quoted outside TON has no TON volume: its native field is
    // zero/foreign units — never convert it with a rate. Only the backend's
    // stamped USD window aggregate is honest for such a row.
    volNative: isNonTonQuote(t.primary_quote_token) ? null : (t.volume_24h_native ?? null),
    volQuote: null,
    volUsd: isNonTonQuote(t.primary_quote_token) ? (t.volume_window_usd ?? null) : null,
    quoteTicker: null,
    quoteToken: t.primary_quote_token ?? null,
    priceUsd: t.price_usd ?? null,
    lastPrice: t.last_price ?? null,
    liq: t.liquidity_usd ?? null,
    discoveredAt: t.birth_ts,
  }
}

// A search hit is one pool of a token, and the backend serves every field the
// row needs — identity (dex, pool_addr), its own liquidity, volume, mcap, 24h
// change and age — so the row renders complete with no per-pool /v1/pairs
// round-trip (that mis-resolves STON.fi v1 pools whose address is also an LP
// jetton master, leaving mcap/vol blank).
export function rowFromHit(h: SearchHit): Row {
  return {
    master: h.jetton_master,
    pool: h.pool_addr || h.jetton_master,
    ticker: displayTicker(h.ticker, h.jetton_master),
    name: h.name,
    imageUrl: canonicalizeMirroredTokenAssetURL(h.dex_image_url) ?? safeImage(h.image_url, true),
    socialLinks: h.social_links,
    dex: h.dex,
    launchpadSource: h.launchpad_source ?? null,
    launchpadStatus: h.launchpad_status ?? null,
    bondingPct: h.bonding_progress_pct ?? null,
    mcap: h.mcap_usd ?? null,
    change: h.price_change_24h_pct ?? null,
    priceWindow: h.price_window,
    volNative: null,
    volQuote: null,
    volUsd: h.volume_24h_usd ?? null,
    // Pools without TON name their quote on the row ("/ USD₮"); TON quotes
    // carry no label.
    quoteTicker: quoteLabel(h.quote_token, h.quote_ticker),
    quoteToken: h.quote_token ?? null,
    priceUsd: null,
    lastPrice: null,
    liq: h.liquidity_usd ?? null,
    discoveredAt: h.birth_ts,
  }
}

// Search rows are atomic identities: an incomplete API hit stays behind the
// status skeleton until the DATA lane has published human labels and a local
// image. During a rolling API/frontend deploy, infer readiness for an older
// payload only when every rendered field is already complete; an explicit
// false always wins.
export function searchHitIdentityReady(h: SearchHit): boolean {
  if (h.metadata_resolved === false) return false
  if (!shouldDisplayTicker(h.ticker, h.jetton_master)) return false
  if (!shouldDisplayTicker(h.name, h.jetton_master)) return false
  if (h.name.trim().toLowerCase() === 'new launchpad token') return false
  return (
    canonicalizeMirroredTokenAssetURL(h.dex_image_url) != null ||
    canonicalizeMirroredTokenAssetURL(h.image_url) != null
  )
}

export function searchPriceWindowTitle(window?: PriceWindowSnapshot): string {
  if (!window) return 'Price window metadata unavailable'
  const asOf = new Date(window.as_of).toLocaleString()
  if (window.status === 'ok') {
    const baseline = window.baseline_source_ts
      ? new Date(window.baseline_source_ts).toLocaleString()
      : 'unknown time'
    return `24h change from the last known price at or before the window start (${baseline}); as of ${asOf}`
  }
  if (window.status === 'since_birth') {
    const baseline = window.baseline_source_ts
      ? new Date(window.baseline_source_ts).toLocaleString()
      : 'unknown time'
    const basis =
      window.baseline_kind === 'pool_birth'
        ? window.pct_denom === 'native'
          ? 'from launch price in GRAM at pool creation'
          : 'from launch price in USD at pool creation'
        : window.pct_denom === 'native'
          ? 'from launch price in GRAM at pool creation'
          : window.baseline_source_lt == null
            ? 'from launch price at token creation'
            : 'since first price after token creation'
    return `24h change ${basis} (${baseline}); as of ${asOf}`
  }
  if (window.status === 'since_first_trade') {
    const baseline = window.baseline_source_ts
      ? new Date(window.baseline_source_ts).toLocaleString()
      : 'unknown time'
    return `24h change from the first successful trade after token creation (${baseline}); as of ${asOf}`
  }
  if (window.status === 'since_recovery') {
    const baseline = window.baseline_source_ts
      ? new Date(window.baseline_source_ts).toLocaleString()
      : 'unknown time'
    if (recoveryClipsWindowStart(window)) {
      const recovery = new Date(window.effective_start as string).toLocaleString()
      return `24h change since durable data recovery (${recovery}), from the first successful post-recovery price (${baseline}); as of ${asOf}`
    }
    return `24h change from the earliest price in this window that is inside the supported data range (${baseline}); no usable price exists earlier in the window; as of ${asOf}`
  }
  if (window.status === 'no_baseline') {
    return `No price exists at or before the 24h window start; change is unavailable. As of ${asOf}`
  }
  if (window.status === 'no_price') return `No qualifying price exists as of ${asOf}`
  if (window.status === 'stale') {
    return `A newer live trade arrived after the authoritative 24h snapshot (${asOf}); change is hidden until recalculated.`
  }
  return `24h price window temporarily unavailable as of ${asOf}`
}

// --- helpers ---------------------------------------------------------------

function displayTicker(ticker: string, master: string): string {
  return shouldDisplayTicker(ticker, master) ? ticker : shortAddr(master)
}

// A pasted contract the backend never indexed still has to render and stay
// clickable, so we synthesize a bare hit for it. It carries no identity, so the
// row falls back to displaying the contract's first characters — which is honest
// only once the server has actually said "I don't know this token".
//
// While the search is still in flight `results` is also empty, and synthesizing
// there flashed those contract characters in place of the token's real name for
// the whole debounce + round-trip. Waiting for the answer costs nothing: the
// caller renders its "Searching…" state instead.
export function addressFallbackHits(
  results: SearchHit[],
  addrQuery: string | null,
  loading: boolean,
  error: Error | null = null
): SearchHit[] {
  if (addrQuery && !loading && error == null && results.length === 0) {
    return [{ jetton_master: addrQuery, ticker: '', name: '', kind: 'token' }]
  }
  return results
}

// Render an image only when it's our mirrored asset (privacy / no hotlinking).
// `mirrored` is the record's image_mirrored flag (detail / search hits lack it,
// so the URL-pattern gate alone guards those).
function safeImage(url: string | undefined, mirrored: boolean): string | undefined {
  return mirrored ? canonicalizeMirroredTokenAssetURL(url) : undefined
}

function usd(v: number | null | undefined): string {
  return typeof v === 'number' && Number.isFinite(v) ? `$${formatCompact(v)}` : DASH
}

// A proven flat value is green; only an absent/invalid value is unknown and muted.
export function pctTone(pct: number | null | undefined): string {
  if (pct == null || !Number.isFinite(pct)) return styles.muted ?? ''
  return (pct >= 0 ? styles.up : styles.down) ?? ''
}
